import type { FeatureCollection, Feature, LineString, MultiLineString, Point } from 'geojson';
import type { PipelineNode } from '@/modules/pipelines/api/entity-api';
import type { Entity } from '@/modules/pipelines/model/entity';

export type PipelineFC = FeatureCollection<LineString | MultiLineString>;
export type ZoneFC = FeatureCollection<Point>;

export const emptyPipelinesGeoJson: PipelineFC = {
  type: 'FeatureCollection',
  features: [],
};

export const emptyZonesGeoJson: ZoneFC = {
  type: 'FeatureCollection',
  features: [],
};
export interface ZoneNotice extends Record<string, unknown> {
  assets: string[];
}

function isPipelineGeometry(g?: Entity['geometry']): g is LineString | MultiLineString {
  return g?.type === 'LineString' || g?.type === 'MultiLineString';
}

function isPointGeometry(g?: Entity['geometry']): g is Point {
  return g?.type === 'Point';
}

export function pipelineGraphToGeoJson(pipelines: PipelineNode[]): PipelineFC {
  const features: Feature<LineString | MultiLineString>[] = [];

  for (const pipeline of pipelines) {
    for (const segment of pipeline.segments ?? []) {
      if (!isPipelineGeometry(segment.geometry)) continue;
      features.push({
        type: 'Feature',
        geometry: segment.geometry,
        properties: {
          id: segment._id,
          name: pipeline.name,
          pipelineId: pipeline._id,
          segmentName: segment.name,
        },
      });
    }
  }

  return { type: 'FeatureCollection', features };
}

export function assetJoinKey(asset: Entity): string {
  return asset._id;
}

export function zoneJoinKey(zone: Entity): string {
  return zone._id;
}

// Every asset in the graph, not just plottable ones — the hover card lists assets
// whether or not their zone has a Point, and the metrics panel counts all of them.
export function collectAssetKeys(pipelines: PipelineNode[]): string[] {
  const keys = new Set<string>();
  for (const pipeline of pipelines) {
    for (const region of pipeline.regions ?? []) {
      for (const zone of region.zones ?? []) {
        for (const asset of zone.assets ?? []) keys.add(assetJoinKey(asset));
      }
    }
  }
  return [...keys];
}

export function collectMapEntityKeys(pipelines: PipelineNode[]): string[] {
  const keys = new Set<string>(collectAssetKeys(pipelines));
  for (const pipeline of pipelines) {
    for (const region of pipeline.regions ?? []) {
      for (const zone of region.zones ?? []) keys.add(zoneJoinKey(zone));
    }
  }
  return [...keys];
}

function noticeId(notice: unknown): string | undefined {
  if (notice && typeof notice === 'object' && 'id' in notice) {
    const id = (notice as { id?: unknown }).id;
    return typeof id === 'string' ? id : undefined;
  }
  return undefined;
}

function noticeTimestamp(notice: unknown): number {
  if (!notice || typeof notice !== 'object') return 0;
  const { postedAt, date } = notice as { postedAt?: unknown; date?: unknown };
  const raw = postedAt ?? date;
  if (typeof raw !== 'string' && typeof raw !== 'number') return 0;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}
export function pipelineGraphToZonesGeoJson(
  pipelines: PipelineNode[],
  noticesByEntity?: Map<string, unknown[]>,
): ZoneFC {
  const features: Feature<Point>[] = [];

  for (const pipeline of pipelines) {
    for (const region of pipeline.regions ?? []) {
      for (const zone of region.zones ?? []) {
        if (!isPointGeometry(zone.geometry)) continue;

        const grouped = new Map<string, { notice: unknown; assets: string[] }>();
        let assetCount = 0;
        let anonymous = 0;

        for (const asset of zone.assets ?? []) {
          assetCount += 1;
          const assetName = asset.name || asset._id;

          for (const notice of noticesByEntity?.get(assetJoinKey(asset)) ?? []) {
            const key = noticeId(notice) ?? `__anon:${anonymous++}`;
            const entry = grouped.get(key);
            if (entry) entry.assets.push(assetName);
            else grouped.set(key, { notice, assets: [assetName] });
          }
        }

        for (const notice of noticesByEntity?.get(zoneJoinKey(zone)) ?? []) {
          const key = noticeId(notice) ?? `__anon:${anonymous++}`;
          if (!grouped.has(key)) grouped.set(key, { notice, assets: [] });
        }

        const notices: ZoneNotice[] = [...grouped.values()]
          .map(({ notice, assets }) => ({
            ...(notice as Record<string, unknown>),
            assets: assets.sort((a, b) => a.localeCompare(b)),
          }))
          .sort((a, b) => noticeTimestamp(b) - noticeTimestamp(a));

        features.push({
          type: 'Feature',
          geometry: zone.geometry,
          properties: {
            id: zone._id,
            name: zone.name,
            pipelineId: pipeline._id,
            pipelineName: pipeline.name,
            regionId: region._id,
            regionName: region.name,
            assetCount,
            noticeCount: notices.length,
            hasNotices: notices.length > 0,
            notices: JSON.stringify(notices),
          },
        });
      }
    }
  }

  return { type: 'FeatureCollection', features };
}
